/*
 * MySchoolRx v1 Final — pillar-based workspace (8 pillars · 115 indicators).
 *
 * Exports a single `DiagnosticV1` component used when a diagnostic record carries
 * `question_set_id === "caporilli_v1_final"`. Legacy diagnostics continue to use
 * the existing DiagnosticForm in CaporilliWorkspace.jsx.
 *
 * Renders:
 *   - Sticky compact header (period, completion, status, save indicator)
 *   - Left pillar rail (sticky on desktop) with completion + health per pillar
 *   - Right indicator workspace with type-aware inputs:
 *       yes_no  → Yes / No toggle
 *       scale   → 1..5 buttons
 *       frequency → segmented (Annual / Quarterly / Monthly / Biweekly / Weekly)
 *       percent → number input 0..100
 *       number  → number input
 *     + notes / evidence URL / assigned owner per indicator
 *     + scoring QA banner per indicator flagged for review
 *
 * Executive Clarity design language. Responsive (lg = sidebar+workspace,
 * smaller screens = stacked + collapsible pillar accordion).
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  CheckCircle2, AlertTriangle, Save, ChevronRight, Lock as LockIcon, Info, HelpCircle, X,
  Paperclip, Download, Trash2, Loader2,
} from "lucide-react";
import EvidenceAttestationModal, { useDataSafetyAcks } from "./DataSafetyComponents";
import PeriodNameGate from "./PeriodNameGate";
import { ScoringUpdateBanner } from "@/components/ScoringUpdateBanner";
import { ScoringComparisonCard } from "@/components/ScoringComparisonCard";

const API = process.env.REACT_APP_BACKEND_URL;
const getSession = () => { try { return JSON.parse(localStorage.getItem("diag_session") || "null"); } catch { return null; } };
const getHdrs = () => {
  const s = getSession();
  if (!s?.user?.id || !s?.tenant?.id) return null;
  return { "x-tenant-id": s.tenant.id, "x-user-id": s.user.id, "x-user-role": s.user.role || "tenant_admin" };
};

const RISK_CLASS = {
  "Strong / Board-ready":                  "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]",
  "Stable but needs targeted improvement": "text-[#1e40af] bg-[#eff6ff] border-[#bfdbfe]",
  "Moderate risk":                         "text-[#a16207] bg-[#fefce8] border-[#fde047]",
  "High risk":                             "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]",
  "Immediate corrective action needed":    "text-[#7f1d1d] bg-[#fef2f2] border-[#fecaca]",
};

const FREQ_LABEL = { annual: "Annual", quarterly: "Quarterly", monthly: "Monthly", biweekly: "Biweekly", weekly: "Weekly" };

const isUnanswered = (r) => !r || (!r.is_not_applicable && (r.raw_value === undefined || r.raw_value === null || r.raw_value === ""));

// First unanswered indicator in pillar order, starting at pillar index `from`; N/A pillars skipped.
const firstUnanswered = (schema, responses, naPillars, from) => {
  const na = new Set(naPillars || []);
  for (const p of (schema.pillars || []).slice(from)) {
    if (na.has(p.id)) continue;
    const hit = (schema.indicators || []).find((i) => i.pillar_id === p.id && isUnanswered(responses[i.id]));
    if (hit) return hit;
  }
  return null;
};

const focusIndicator = (id, delay = 350) => setTimeout(() => {
  const el = document.querySelector(`[data-indicator-id="${id}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-[#475569]", "ring-offset-2");
  setTimeout(() => el.classList.remove("ring-2", "ring-[#475569]", "ring-offset-2"), 2400);
}, delay);

const DiagnosticV1 = ({ diagnosticId }) => {
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [diag, setDiag] = useState(null);
  const [scoringVersion, setScoringVersion] = useState(null);
  const [showScoringNotice, setShowScoringNotice] = useState(false);
  const [versionComparison, setVersionComparison] = useState(null);
  const dismissScoringNotice = () => {
    setShowScoringNotice(false);
    axios.post(`${API}/api/diagnostics/${diagnosticId}/scoring-notice/dismiss`, {}, { headers: getHdrs() }).catch(() => {});
  };
  const jumpToIndicator = (id) => {
    const ind = schema?.indicators?.find((i) => i.id === id);
    if (!ind) return;
    setActive(ind.pillar_id);
    focusIndicator(id, 450);
  };
  const [finalizing, setFinalizing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [responses, setResponses] = useState({});   // indicator_id → row
  const [active, setActive] = useState(null);       // active pillar id
  const [saving, setSaving] = useState({});         // indicator_id → bool
  const [helpByIndicator, setHelpByIndicator] = useState({}); // indicator_id → help row
  const [openHelpId, setOpenHelpId] = useState(null);
  const [attachmentCounts, setAttachmentCounts] = useState({}); // indicator_id → count
  const sectionRef = useRef(null);

  // Phase 7 — Deep-link from Heat Map: /diagnostic/form?cat=pillar_id (or ?pillar=)
  // 6/3 update — also supports ?next=1 (jump to next open question across all pillars)
  // and ?q=<indicator_id> (scroll directly to that indicator).
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const target = sp.get("pillar") || sp.get("cat");
      if (target) setActive(target);
    } catch { /* noop */ }
  }, []);

  // 6/3 update — when ?next=1 is set, find the first unanswered (and non-N/A)
  // indicator across all pillars and route to it. If everything is complete,
  // fall back to the first incomplete pillar (or stay where the user landed).
  useEffect(() => {
    if (!schema?.indicators?.length) return;
    let nextFlag = false;
    let qParam = null;
    try {
      const sp = new URLSearchParams(window.location.search);
      nextFlag = sp.get("next") === "1";
      qParam = sp.get("q");
    } catch { /* noop */ }
    if (!nextFlag && !qParam) return;
    const indicators = schema.indicators || [];
    const targetInd = qParam
      ? indicators.find((i) => i.id === qParam)
      : indicators.find((i) => {
          const r = responses[i.id];
          if (!r) return true;
          if (r.is_not_applicable) return false;
          return r.raw_value === undefined || r.raw_value === null || r.raw_value === "";
        });
    if (targetInd) {
      setActive(targetInd.pillar_id);
      // Scroll after the workspace re-renders the new pillar's indicators
      setTimeout(() => {
        const el = document.querySelector(`[data-indicator-id="${targetInd.id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-[#007a9e]", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-[#007a9e]", "ring-offset-2"), 2400);
        }
      }, 350);
    }
    // Clear the URL params so refresh doesn't re-scroll endlessly
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("next");
      url.searchParams.delete("q");
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, responses]);

  const refreshAttachmentCounts = useCallback(async () => {
    const h = getHdrs(); if (!h) return;
    try {
      const r = await axios.get(`${API}/api/diagnostics/${diagnosticId}/attachments/counts`, { headers: h });
      setAttachmentCounts(r.data?.counts || {});
    } catch { /* ignore */ }
  }, [diagnosticId]);

  useEffect(() => { refreshAttachmentCounts(); }, [refreshAttachmentCounts]);

  const reload = useCallback(async () => {
    const h = getHdrs(); if (!h) return;
    try {
      const [s, full] = await Promise.all([
        axios.get(`${API}/api/diagnostics/question-set/active`),
        axios.get(`${API}/api/diagnostics/${diagnosticId}/v1`, { headers: h }),
      ]);
      // Each assessment is shown with the scoring rules of its own scoring version.
      const ov = full.data.indicator_overrides || {};
      setSchema({ ...s.data, indicators: s.data.indicators.map((i) => (ov[i.id] ? { ...i, ...ov[i.id] } : i)) });
      setScoringVersion(full.data.scoring_version);
      setShowScoringNotice(!!full.data.show_scoring_notice);
      setVersionComparison(full.data.version_comparison || null);
      setDiag(full.data.diagnostic);
      setSummary(full.data.summary);
      const idx = {};
      for (const r of full.data.responses || []) idx[r.indicator_id] = r;
      setResponses(idx);
      // Phase 7 — Honor ?cat=/?pillar= deep link; only fall back to first pillar
      // when neither the URL param nor existing state has set a target.
      if (!active) {
        let target = null;
        let hasParams = false;
        try {
          const sp = new URLSearchParams(window.location.search);
          target = sp.get("pillar") || sp.get("cat");
          hasParams = !!(target || sp.get("next") || sp.get("q"));
        } catch { /* noop */ }
        // Returning users resume at the first unanswered question (pillar order, N/A pillars skipped).
        const nextInd = !hasParams && Object.keys(idx).length > 0
          ? firstUnanswered(s.data, idx, full.data.diagnostic?.na_pillars, 0) : null;
        if (nextInd) {
          setActive(nextInd.pillar_id);
          focusIndicator(nextInd.id, 450);
        } else {
          setActive(target || s.data.pillars[0]?.id || null);
        }
      }
    } catch (e) {
      toast.error("Failed to load diagnostic");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosticId]);

  useEffect(() => { reload(); }, [reload]);

  // Reset the question-column scroll to the top whenever the pillar changes
  // (targeted resume/deep-link scrolls run afterwards and take precedence).
  useEffect(() => {
    if (active) sectionRef.current?.scrollTo?.({ top: 0 });
  }, [active]);

  // Load help text for all indicators in bulk (published only)
  useEffect(() => {
    if (!schema?.indicators?.length) return;
    const h = getHdrs(); if (!h) return;
    const ids = schema.indicators.map((i) => i.id).join(",");
    axios.get(`${API}/api/indicator-help?indicator_ids=${ids}`, { headers: h })
      .then((r) => setHelpByIndicator(r.data || {}))
      .catch(() => { /* fail silent — popover just won't appear */ });
  }, [schema]);

  const locked = diag?.locked || ["approved", "submitted_for_review"].includes(diag?.status);

  const upsert = async (indicator, patch) => {
    if (locked) return;
    const existing = responses[indicator.id] || {};
    // Phase 3.3 — default assigned_owner to logged-in user when blank on save
    const session = getSession();
    const meName = session?.user?.full_name || session?.user?.email || "";
    const incomingOwner = patch.assigned_owner !== undefined ? patch.assigned_owner : existing.assigned_owner;
    const resolvedOwner = (incomingOwner && String(incomingOwner).trim()) ? incomingOwner : meName;
    const body = {
      indicator_id: indicator.id,
      raw_value: patch.raw_value !== undefined ? patch.raw_value : existing.raw_value,
      is_not_applicable: patch.is_not_applicable !== undefined ? patch.is_not_applicable : (existing.is_not_applicable || false),
      notes: patch.notes !== undefined ? patch.notes : (existing.notes || ""),
      evidence_url: patch.evidence_url !== undefined ? patch.evidence_url : (existing.evidence_url || ""),
      assigned_owner: resolvedOwner || "",
    };
    setSaving((p) => ({ ...p, [indicator.id]: true }));
    try {
      const r = await axios.put(`${API}/api/diagnostics/${diagnosticId}/responses-v2`, body, { headers: getHdrs() });
      setResponses((prev) => ({ ...prev, [indicator.id]: r.data.response }));
      setSummary(r.data.summary);
      if (r.data.version_comparison) setVersionComparison(r.data.version_comparison);
      setDiag((p) => p ? { ...p, status: r.data.diagnostic_status } : p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((p) => ({ ...p, [indicator.id]: false }));
    }
  };

  const togglePillarNa = async (pillarId, isNa) => {
    if (locked) return;
    try {
      const r = await axios.post(`${API}/api/diagnostics/${diagnosticId}/pillar-na`,
        { pillar_id: pillarId, is_not_applicable: isNa },
        { headers: getHdrs() }
      );
      setSummary(r.data.summary);
      toast.success(isNa ? "Pillar marked Not Applicable" : "Pillar re-included");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const pillarIndicators = useMemo(() => {
    if (!schema || !active) return [];
    return schema.indicators.filter((i) => i.pillar_id === active);
  }, [schema, active]);

  // Auto-advance: after a single-tap answer, smooth-scroll to the next question
  // (or the Next-pillar button if it was the last question in the pillar).
  const advanceFrom = useCallback((indicatorId) => {
    const idx = pillarIndicators.findIndex((i) => i.id === indicatorId);
    if (idx === -1) return;
    const next = pillarIndicators[idx + 1];
    setTimeout(() => {
      if (next) {
        const el = document.querySelector(`[data-indicator-id="${next.id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-[#007a9e]", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-[#007a9e]", "ring-offset-2"), 1200);
        }
      } else {
        document.querySelector('[data-testid="diag-v1-next-pillar"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
  }, [pillarIndicators]);

  const pillarSummary = summary?.pillar_summaries?.find((p) => p.id === active);
  const activePillar = schema?.pillars?.find((p) => p.id === active);

  if (!schema || !diag || !summary) {
    return <div className="px-8 py-20 text-center text-[#6b7280]">Loading MySchoolRx…</div>;
  }

  return (
    <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6" data-testid="diag-v1">
      {showScoringNotice && <ScoringUpdateBanner onDismiss={dismissScoringNotice} onJump={jumpToIndicator} />}
      <ScoringComparisonCard comparison={versionComparison} onJump={jumpToIndicator} />
      {!locked && diag.perspective !== "portfolio" && (diag.needs_period_name || !(diag.period_label || "").trim()) && (
        <PeriodNameGate diag={diag} headers={getHdrs()} onNamed={(d) => setDiag((p) => ({ ...p, ...d }))} />
      )}
      {diag.perspective === "portfolio" && (
        <div className="mb-4 rounded-xl overflow-hidden border border-[#d9ccec]" data-testid="diag-v1-portfolio-perspective">
          <div className="bg-[#6b3fa0] px-4 py-3 text-white font-semibold" data-testid="diag-v1-portfolio-header">
            Your Portfolio Assessment of {(() => { try { const s = JSON.parse(localStorage.getItem("diag_session") || "null"); return s?.tenant?.school_name || s?.tenant?.name || "this school"; } catch { return "this school"; } })()}
          </div>
          <div className="bg-[#f5f0fb] px-4 py-2.5 text-[13px] font-semibold text-[#4c2a78]" data-testid="diag-v1-portfolio-private">
            Private to your Portfolio. The school never sees it.
          </div>
        </div>
      )}
      {/* Sticky executive header */}
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#f9fafb] border-b border-[#e5e7eb] mb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <button onClick={() => navigate("/diagnostic")} className="text-[12px] text-[#6b7280] hover:text-[#111827]">← Back to dashboard</button>
            <h1 className="text-lg font-bold text-[#111827] truncate">{diag.period_label || "MySchoolRx"}</h1>
            <div className="text-[11px] text-[#6b7280]">
              A comprehensive health panel for K-12 schools
              {scoringVersion && <span className="ml-2" data-testid="diag-v1-scoring-version">· Scoring v{scoringVersion}</span>}
              {scoringVersion === "1.1" && !showScoringNotice && (
                <button type="button" onClick={() => setShowScoringNotice(true)} className="ml-2 font-semibold text-[#00698a] underline underline-offset-2" data-testid="diag-v1-what-changed">What changed?</button>
              )}
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-[12px] justify-start sm:justify-end max-w-full">
            <Stat label="Completion" value={`${summary.completion_percentage}%`} />
            <Stat label="Answered" value={`${summary.total_answered}/${summary.total_indicators}`} />
            <Stat label="Overall" value={summary.overall_score != null ? `${summary.overall_score}/100` : "—"} />
            <Stat label="Risk" value={summary.overall_risk_level || "—"} />
            {summary.overall_score != null && (
              (diag?.locked || diag?.status === "approved") ? (
                <span data-testid="diag-v1-score-status" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#065f46] bg-[#ecfdf5] border border-[#a7f3d0] px-2 py-1 rounded">
                  Final
                </span>
              ) : (
                <span data-testid="diag-v1-score-status" title="Score is provisional and updates as you answer; it becomes Final after submission and approval." className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#a16207] bg-white border border-[#fde047] px-2 py-1 rounded">
                  In Progress · Provisional
                </span>
              )
            )}
            {locked && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#065f46] bg-[#ecfdf5] border border-[#a7f3d0] px-2 py-1 rounded">
                <LockIcon className="h-3 w-3" /> Read only
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 lg:h-[calc(100vh-150px)]" data-testid="diag-v1-grid">
        {/* Pillar rail — independent scroll on desktop so header + 8-pillar nav stay visible */}
        <aside className="lg:h-full lg:overflow-y-auto lg:self-start lg:pr-1" data-testid="diag-v1-rail">
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-2.5 space-y-1">
            {schema.pillars.map((p) => {
              const ps = summary.pillar_summaries.find((x) => x.id === p.id);
              const isActive = p.id === active;
              const isNa = ps?.is_not_applicable;
              const riskKey = ps?.risk_level;
              const riskCls = riskKey ? RISK_CLASS[riskKey] : "";
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${
                    isActive ? "bg-[#0b1220] text-white" : "hover:bg-[#f9fafb] text-[#374151]"
                  } ${isNa ? "opacity-60" : ""}`}
                  data-testid={`diag-v1-rail-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[13px] font-semibold leading-tight">{p.name}</span>
                    {ps?.completion_pct >= 100 && <CheckCircle2 className="h-4 w-4 text-[#10b981] shrink-0" />}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className={`text-[10px] ${isActive ? "text-white/60" : "text-[#6b7280]"}`}>
                      {isNa ? "Not Applicable" : `${ps?.completion_pct ?? 0}% · ${ps?.questions_answered ?? 0}/${ps?.questions_total ?? 0}`}
                    </span>
                    {!isNa && ps?.health_score != null && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${riskCls}`}>
                        {ps.health_score}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {activePillar?.is_optional && (
            <div className="mt-3 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3" data-testid="diag-v1-pillar-na-toggle">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6b7280]">Optional pillar</div>
              <p className="text-[12px] text-[#374151] mt-1 leading-relaxed">
                If this pillar doesn't apply to your school, mark it Not Applicable to exclude it from overall scoring.
              </p>
              <Button
                size="sm"
                variant="outline"
                disabled={locked}
                onClick={() => togglePillarNa(active, !pillarSummary?.is_not_applicable)}
                className="mt-2 w-full border-[#e5e7eb] text-[12px]"
                data-testid="diag-v1-pillar-na-btn"
              >
                {pillarSummary?.is_not_applicable ? "Re-include pillar" : "Mark pillar Not Applicable"}
              </Button>
            </div>
          )}
        </aside>

        {/* Indicator workspace — the only region that scrolls on desktop */}
        <section ref={sectionRef} className="space-y-4 min-w-0 lg:h-full lg:overflow-y-auto lg:pr-2 lg:pb-4">
          {/* Pillar header */}
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-[#00698a] font-semibold">Pillar {activePillar?.display_order}</div>
                <h2 className="text-2xl font-bold text-[#111827] leading-tight">{activePillar?.name}</h2>
                <p className="mt-2 text-[13px] text-[#4b5563] leading-relaxed max-w-3xl">{activePillar?.description}</p>
              </div>
              {pillarSummary && !pillarSummary.is_not_applicable && (
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-[12px] sm:shrink-0 max-w-full">
                  <Stat label="Completion" value={`${pillarSummary.completion_pct}%`} />
                  <Stat label="Points" value={`${pillarSummary.earned_points}/${pillarSummary.max_possible_points}`} />
                  <Stat label="Health" value={pillarSummary.health_score != null ? `${pillarSummary.health_score}/100` : "—"} />
                  {pillarSummary.risk_level && (
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded border max-w-full whitespace-normal ${RISK_CLASS[pillarSummary.risk_level]}`}>
                      {pillarSummary.risk_level}
                    </span>
                  )}
                </div>
              )}
            </div>
            {pillarSummary?.is_not_applicable && (
              <div className="mt-3 rounded-md border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2 text-[13px] text-[#1e3a8a]">
                This pillar is currently marked Not Applicable and excluded from the overall score.
              </div>
            )}
          </div>

          {/* Indicators */}
          {!pillarSummary?.is_not_applicable && pillarIndicators.map((ind) => (
            <IndicatorCard
              key={ind.id}
              indicator={ind}
              response={responses[ind.id] || {}}
              locked={locked}
              saving={!!saving[ind.id]}
              help={helpByIndicator[ind.id]}
              attachmentCount={attachmentCounts[ind.id] || 0}
              diagnosticId={diagnosticId}
              onAttachmentsChanged={refreshAttachmentCounts}
              onOpenHelp={() => setOpenHelpId(ind.id)}
              onChange={(patch) => upsert(ind, patch)}
              onAdvance={() => advanceFrom(ind.id)}
            />
          ))}

          {/* Footer nav */}
          <div className="flex items-center justify-between pt-3">
            <Button
              variant="outline"
              disabled={schema.pillars.findIndex((p) => p.id === active) === 0}
              onClick={() => {
                const i = schema.pillars.findIndex((p) => p.id === active);
                if (i > 0) setActive(schema.pillars[i - 1].id);
                sectionRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="border-[#e5e7eb] text-[#111827]"
            >
              ← Previous pillar
            </Button>
            <div className="text-[11px] text-[#9ca3af] flex items-center gap-1.5">
              {locked ? <><LockIcon className="h-3 w-3" /> Locked</> : <><Save className="h-3 w-3" /> Auto-saved</>}
            </div>
            {(() => {
              const last = schema.pillars.findIndex((p) => p.id === active) === schema.pillars.length - 1;
              const pf = diag.perspective === "portfolio";
              const left = Math.max(0, (summary.total_indicators || 0) - (summary.total_answered || 0));
              const canFinalize = pf && last && !locked && left === 0;
              const label = !last ? "Next pillar →" : canFinalize ? "Finish & mark final" : pf && !locked ? "Save & exit" : "Finish — back to dashboard";
              return (
                <div className="flex flex-col items-end gap-1">
                  <Button
                    disabled={finalizing}
                    onClick={async () => {
                      const i = schema.pillars.findIndex((p) => p.id === active);
                      const nxt = i < schema.pillars.length - 1 ? firstUnanswered(schema, responses, diag.na_pillars, i + 1) : null;
                      if (nxt) { setActive(nxt.pillar_id); focusIndicator(nxt.id, 450); return; }
                      if (i < schema.pillars.length - 1) setActive(schema.pillars[i + 1].id);
                      else if (canFinalize) {
                        setFinalizing(true);
                        try {
                          await axios.post(`${API}/api/diagnostics/${diagnosticId}/submit`, {}, { headers: getHdrs() });
                          toast.success("Portfolio assessment marked final");
                          navigate(`/portfolio/schools/${diag.tenant_id}`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Couldn't mark the assessment final");
                        } finally { setFinalizing(false); }
                      } else navigate(pf ? `/portfolio/schools/${diag.tenant_id}` : "/diagnostic");
                      sectionRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={canFinalize ? "bg-[#6b3fa0] hover:bg-[#563285] text-white" : "bg-[#007a9e] hover:bg-[#00607d] text-white"}
                    data-testid={canFinalize ? "diag-v1-finalize" : "diag-v1-next-pillar"}
                  >
                    {finalizing && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{label}
                  </Button>
                  {pf && last && !locked && left > 0 && (
                    <span className="text-[11.5px] text-[#6b7280]" data-testid="diag-v1-questions-left">{left} question{left === 1 ? "" : "s"} left before you can mark it final</span>
                  )}
                </div>
              );
            })()}
          </div>
        </section>
      </div>
      {/* Help popover modal */}
      {openHelpId && (
        <IndicatorHelpModal
          help={helpByIndicator[openHelpId]}
          indicator={schema.indicators.find((i) => i.id === openHelpId)}
          onClose={() => setOpenHelpId(null)}
        />
      )}
    </div>
  );
};

const IndicatorHelpModal = ({ help, indicator, onClose }) => {
  if (!help || !indicator) return null;
  const FIELDS = [
    { key: "explanation", label: "What this means" },
    { key: "why_it_matters", label: "Why it matters" },
    { key: "where_to_find_data", label: "Where to find the data" },
    { key: "example_evidence", label: "Example evidence" },
    { key: "suggested_responsible_party", label: "Suggested owner" },
    { key: "common_mistake", label: "Common mistake to avoid" },
    { key: "annual_report_relevance", label: "Annual report relevance" },
  ];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white shadow-2xl w-full sm:max-w-xl h-full sm:h-auto sm:max-h-[90vh] sm:rounded-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="diag-v1-help-modal"
      >
        <div className="px-5 py-3 border-b border-[#e5e7eb] flex items-start justify-between gap-3 sticky top-0 bg-white">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-[#3B4FE4] font-semibold">Help</div>
            <h3 className="text-base font-bold text-[#111827] leading-snug">{indicator.indicator_name}</h3>
          </div>
          <button onClick={onClose} className="text-[#6b7280] hover:text-[#111827]" data-testid="diag-v1-help-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-5">
          {FIELDS.map((f) => help[f.key] && (
            <div key={f.key} data-testid={`diag-v1-help-field-${f.key}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">{f.label}</div>
              <p className="text-[13px] text-[#374151] leading-relaxed mt-1.5 mb-1">{help[f.key]}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">{label}</div>
    <div className="mt-0.5 text-[13px] font-bold text-[#111827]">{value}</div>
  </div>
);

const IndicatorCard = ({ indicator, response, locked, saving, help, attachmentCount, diagnosticId, onAttachmentsChanged, onOpenHelp, onChange, onAdvance }) => {
  const inputType = indicator.input_type;
  const isNa = !!response.is_not_applicable;
  const earned = isNa ? null : response.calculated_points;
  const mx = indicator.max_points;
  const flagged = indicator.scoring_review_required;
  const hasHelp = !!help;
  const bands = Array.isArray(indicator.answer_bands) ? indicator.answer_bands : [];
  const hasBands = bands.length > 0;
  const numVal = (!isNa && response.raw_value != null && response.raw_value !== "") ? Number(response.raw_value) : null;
  const bandMatches = (b) => numVal != null && (b.lo == null || numVal >= b.lo) && (b.hi == null || numVal < b.hi);

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid={`diag-v1-q-${indicator.id}`} data-indicator-id={indicator.id}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#f3f4f6] text-[#6b7280] text-xs font-bold shrink-0">
          {indicator.display_order}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-[15px] font-semibold text-[#111827] leading-snug">{indicator.indicator_name}</p>
            {hasHelp && (
              <button
                onClick={onOpenHelp}
                className="inline-flex items-center justify-center h-5 w-5 rounded-full text-[#3B4FE4] hover:bg-[#eff6ff] transition-colors"
                title="What does this mean?"
                data-testid={`diag-v1-help-icon-${indicator.id}`}
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-[12px] text-[#6b7280] mt-0.5">{indicator.what_to_enter}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">Pts</div>
          <div className="text-[13px] font-bold text-[#111827]">
            {isNa ? "N/A" : (earned != null ? earned : "—")}/{mx}
          </div>
          {saving && <div className="text-[10px] text-[#00698a]">Saving…</div>}
        </div>
      </div>

      {flagged && (
        <div className="mt-3 rounded-md border border-[#fde047] bg-[#fefce8] px-3 py-2 text-[12px] text-[#a16207] flex items-start gap-2" data-testid={`diag-v1-qa-flag-${indicator.id}`}>
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div><strong className="font-semibold">Scoring under Super Admin review.</strong> {indicator.scoring_review_note}</div>
        </div>
      )}

      {/* Input — type-aware */}
      <div className="mt-4">
        {hasBands && (
          <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label={indicator.indicator_name} data-testid={`diag-v1-bands-${indicator.id}`}>
            {bands.map((b) => {
              const sel = bandMatches(b);
              return (
                <button
                  key={b.key}
                  type="button"
                  role="radio"
                  aria-checked={sel}
                  disabled={locked}
                  onClick={() => { onChange({ raw_value: b.value, is_not_applicable: false }); onAdvance?.(); }}
                  className={`min-w-[64px] h-10 px-4 rounded-md border text-[13px] font-semibold transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B4FE4]/50 ${
                    sel ? "bg-[#007a9e] border-[#007a9e] text-white" : "bg-white border-[#e5e7eb] text-[#374151] hover:border-[#007a9e]/40"
                  }`}
                  data-testid={`diag-v1-band-${indicator.id}-${b.key}`}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        )}
        {inputType === "yes_no" && (
          <div className="flex gap-2">
            {[
              { v: true, label: "Yes" },
              { v: false, label: "No" },
            ].map((o) => {
              const sel = response.raw_value === o.v && !isNa;
              return (
                <button
                  key={String(o.v)}
                  type="button"
                  disabled={locked}
                  onClick={() => { onChange({ raw_value: o.v, is_not_applicable: false }); onAdvance?.(); }}
                  className={`px-4 py-1.5 rounded-md border text-[13px] font-semibold transition-colors disabled:opacity-50 ${
                    sel ? "bg-[#007a9e] border-[#007a9e] text-white" : "bg-white border-[#e5e7eb] text-[#374151] hover:border-[#007a9e]/40"
                  }`}
                  data-testid={`diag-v1-yes-no-${indicator.id}-${String(o.v)}`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
        {inputType === "scale" && (
          <div className="inline-flex flex-col gap-1">
          <div className="flex gap-2 flex-wrap">
            {[1, 2, 3, 4, 5].map((v) => {
              const sel = response.raw_value === v && !isNa;
              return (
                <button
                  key={v}
                  type="button"
                  disabled={locked}
                  onClick={() => { onChange({ raw_value: v, is_not_applicable: false }); onAdvance?.(); }}
                  className={`min-w-[40px] px-3 py-1.5 rounded-md border text-[13px] font-semibold transition-colors disabled:opacity-50 ${
                    sel ? "bg-[#007a9e] border-[#007a9e] text-white" : "bg-white border-[#e5e7eb] text-[#374151] hover:border-[#007a9e]/40"
                  }`}
                  data-testid={`diag-v1-scale-${indicator.id}-${v}`}
                >
                  {v}
                </button>
              );
            })}
          </div>
            {indicator.scale_endpoints && (
              <div className="flex justify-between gap-3 text-[12px] text-[#4b5563]" data-testid={`diag-v1-scale-endpoints-${indicator.id}`}>
                <span>{indicator.scale_endpoints.low}</span><span>{indicator.scale_endpoints.high}</span>
              </div>
            )}
          </div>
        )}
        {inputType === "frequency" && (
          <div className="flex gap-2 flex-wrap">
            {["annual", "quarterly", "monthly", "biweekly", "weekly"].map((f) => {
              const sel = response.raw_value === f && !isNa;
              return (
                <button
                  key={f}
                  type="button"
                  disabled={locked}
                  onClick={() => { onChange({ raw_value: f, is_not_applicable: false }); onAdvance?.(); }}
                  className={`px-3 py-1.5 rounded-md border text-[12px] font-semibold transition-colors disabled:opacity-50 ${
                    sel ? "bg-[#007a9e] border-[#007a9e] text-white" : "bg-white border-[#e5e7eb] text-[#374151] hover:border-[#007a9e]/40"
                  }`}
                  data-testid={`diag-v1-freq-${indicator.id}-${f}`}
                >
                  {FREQ_LABEL[f]}
                </button>
              );
            })}
          </div>
        )}
        {!hasBands && inputType === "percent" && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              defaultValue={!isNa && response.raw_value != null ? response.raw_value : ""}
              disabled={locked}
              onBlur={(e) => {
                const v = e.target.value === "" ? null : Number(e.target.value);
                onChange({ raw_value: v, is_not_applicable: false });
              }}
              className="h-10 w-28 bg-white border-[#e5e7eb] text-[#111827] text-[14px] font-semibold"
              data-testid={`diag-v1-percent-${indicator.id}`}
            />
            <span className="text-[13px] text-[#6b7280]">%</span>
          </div>
        )}
        {!hasBands && inputType === "number" && (
          <Input
            type="number"
            min="0"
            step="any"
            defaultValue={!isNa && response.raw_value != null ? response.raw_value : ""}
            disabled={locked}
            onBlur={(e) => {
              const v = e.target.value === "" ? null : Number(e.target.value);
              onChange({ raw_value: v, is_not_applicable: false });
            }}
            className="h-10 w-32 bg-white border-[#e5e7eb] text-[#111827] text-[14px] font-semibold"
            data-testid={`diag-v1-number-${indicator.id}`}
          />
        )}
        <button
          type="button"
          disabled={locked}
          onClick={() => onChange({ is_not_applicable: !isNa, raw_value: null })}
          className={`mt-2 text-[11px] font-semibold underline ${
            isNa ? "text-[#6b7280]" : "text-[#9ca3af] hover:text-[#374151]"
          } disabled:opacity-50`}
          data-testid={`diag-v1-na-${indicator.id}`}
        >
          {isNa ? "Re-include this indicator" : "Mark Not Applicable"}
        </button>
      </div>

      {/* Notes / evidence / owner */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1fr] gap-3">
        <Textarea
          defaultValue={response.notes || ""}
          disabled={locked}
          onBlur={(e) => onChange({ notes: e.target.value })}
          placeholder="Notes / context"
          className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] min-h-[60px] disabled:opacity-60"
          data-testid={`diag-v1-notes-${indicator.id}`}
        />
        <Input
          defaultValue={response.evidence_url || ""}
          disabled={locked}
          onBlur={(e) => onChange({ evidence_url: e.target.value })}
          placeholder="Evidence URL / file link"
          className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] h-10 disabled:opacity-60"
          data-testid={`diag-v1-evidence-${indicator.id}`}
        />
        <Input
          defaultValue={response.assigned_owner || ""}
          disabled={locked}
          onBlur={(e) => onChange({ assigned_owner: e.target.value })}
          placeholder="Assigned owner"
          className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] h-10 disabled:opacity-60"
          data-testid={`diag-v1-owner-${indicator.id}`}
        />
      </div>
      {!response.evidence_url && !isNa && (response.raw_value != null) && (attachmentCount || 0) === 0 && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#a16207]" data-testid={`diag-v1-evidence-missing-${indicator.id}`}>
          <AlertTriangle className="h-3 w-3" /> Evidence Missing
        </div>
      )}

      {/* File Attachments (Layer 3 — evidence files per indicator) */}
      <AttachmentsBlock
        indicator={indicator}
        diagnosticId={diagnosticId}
        locked={locked}
        count={attachmentCount}
        onChanged={onAttachmentsChanged}
      />
    </div>
  );
};

const ALLOWED_EXT_LIST = ["pdf", "docx", "xlsx", "png", "jpg", "jpeg", "csv", "txt"];
const ACCEPT_ATTR = ALLOWED_EXT_LIST.map((e) => "." + e).join(",");

const AttachmentsBlock = ({ indicator, diagnosticId, locked, count, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attestOpen, setAttestOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const inputRef = useRef(null);
  const acks = useDataSafetyAcks();

  const load = async () => {
    const h = getHdrs(); if (!h) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/diagnostics/${diagnosticId}/indicators/${indicator.id}/attachments`,
        { headers: h }
      );
      setItems(r.data || []);
    } catch (e) {
      toast.error("Failed to load attachments");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT_LIST.includes(ext)) {
      toast.error(`Only ${ALLOWED_EXT_LIST.join(", ")} files are allowed.`);
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit.");
      return;
    }
    // Stage file and open Aggregate Data Safety attestation modal.
    setPendingFile(f);
    setAttestOpen(true);
  };

  const performUpload = async (f) => {
    if (!f) return;
    const h = getHdrs(); if (!h) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("attestation_confirmed", "true");
    fd.append("attestation_text_version", "2.0-aggregate-data-safety");
    setUploading(true);
    try {
      await axios.post(
        `${API}/api/diagnostics/${diagnosticId}/indicators/${indicator.id}/attachments`,
        fd,
        { headers: { ...h, "Content-Type": "multipart/form-data" } }
      );
      toast.success("Evidence file uploaded");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleAttestationConfirm = async () => {
    setAttestOpen(false);
    const file = pendingFile;
    setPendingFile(null);
    if (file) await performUpload(file);
  };

  const handleAttestationCancel = () => {
    setAttestOpen(false);
    setPendingFile(null);
  };

  const onDownload = async (att) => {
    const h = getHdrs(); if (!h) return;
    try {
      const r = await axios.get(
        `${API}/api/diagnostics/${diagnosticId}/attachments/${att.id}/download`,
        { headers: h, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = att.original_file_name || att.file_name || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const onDelete = async (att) => {
    if (locked) return;
    if (!window.confirm(`Delete "${att.original_file_name || att.file_name}"?`)) return;
    const h = getHdrs(); if (!h) return;
    try {
      await axios.delete(
        `${API}/api/diagnostics/${diagnosticId}/attachments/${att.id}`,
        { headers: h }
      );
      toast.success("Deleted");
      await load();
      onChanged && onChanged();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-3 border-t border-dashed border-[#e5e7eb] pt-3" data-testid={`diag-v1-attachments-${indicator.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#3B4FE4] hover:text-[#1e3a8a]"
        data-testid={`diag-v1-attach-toggle-${indicator.id}`}
      >
        <Paperclip className="h-3.5 w-3.5" />
        Evidence files
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[#3B4FE4] text-white">
            {count}
          </span>
        )}
        <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-[#e5e7eb] bg-[#fafafa] p-3 space-y-2">
          {loading && <div className="text-[12px] text-[#6b7280]">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-[12px] text-[#6b7280]">No files attached yet. Allowed: {ALLOWED_EXT_LIST.join(", ")}. Max 25 MB.</div>
          )}
          {items.map((att) => (
            <div key={att.id} className="flex items-center gap-2 rounded-md bg-white border border-[#e5e7eb] px-3 py-2">
              <Paperclip className="h-3.5 w-3.5 text-[#6b7280] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#111827] truncate">{att.original_file_name || att.file_name}</div>
                <div className="text-[10px] text-[#6b7280]">{Math.round((att.size_bytes || 0)/1024)} KB · {(att.uploaded_at || "").slice(0, 10)}</div>
              </div>
              <button
                onClick={() => onDownload(att)}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md text-[#3B4FE4] hover:bg-[#eff6ff]"
                title="Download"
                data-testid={`diag-v1-attach-dl-${att.id}`}
              >
                <Download className="h-4 w-4" />
              </button>
              {!locked && (
                <button
                  onClick={() => onDelete(att)}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md text-[#b91c1c] hover:bg-[#fef2f2]"
                  title="Delete"
                  data-testid={`diag-v1-attach-del-${att.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!locked && (
            <div className="pt-1">
              <input
                type="file"
                ref={inputRef}
                accept={ACCEPT_ATTR}
                onChange={onPick}
                className="hidden"
                data-testid={`diag-v1-attach-input-${indicator.id}`}
              />
              <Button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="bg-[#0b1220] hover:bg-[#1f2937] text-white text-[12px] h-8"
                data-testid={`diag-v1-attach-upload-${indicator.id}`}
              >
                {uploading ? "Uploading…" : "Upload file"}
              </Button>
              <span className="ml-2 text-[10px] text-[#6b7280]">Allowed: {ALLOWED_EXT_LIST.join(", ")}. Max 25 MB.</span>
            </div>
          )}
        </div>
      )}
      {/* Aggregate Data Safety attestation modal — gates every upload */}
      <EvidenceAttestationModal
        open={attestOpen}
        pendingFileName={pendingFile?.name || ""}
        ackComplete={!!acks.evidence_upload}
        onAcknowledgeOnboarding={() => acks.acknowledge("evidence_upload")}
        onCancel={handleAttestationCancel}
        onConfirm={handleAttestationConfirm}
      />
    </div>
  );
};

export default DiagnosticV1;
